// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  SlidersHorizontal,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
} from 'lucide-react';
import { transactionAPI } from '../api/services/budget/transactionService';
import categoryAPI from '../api/services/budget/categoryService';
import TransactionList from '../components/transactions/TransactionList';
import TransactionForm from '../components/transactions/TransactionForm';
import GroupUpdateModal from '../components/transactions/GroupUpdateModal';
import { classNames } from '../utils/helpers';
import { getYearOrMonthRange } from '../utils/dates';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import { enqueue, isNetworkError } from '../offline/outbox';
import {
  cacheTransactions,
  getCachedTransactions,
  cacheCategories,
  getCachedCategories,
} from '../offline/db';

const MONTHS = [
  'All',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export default function Transactions() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // ── Filters ───────────────────────────────────────────────────
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [useDateRange, setUseDateRange] = useState(false); // Default to current month
  const [dateFrom, setDateFrom] = useState(
    new Date(currentYear, currentMonth - 1, 1).toISOString().split('T')[0]
  );
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');

  // ── Pagination (#15) ────────────────────────────────────────────
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ count: 0, hasNext: false, hasPrevious: false });

  // ── Data ──────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── UI state ──────────────────────────────────────────────────
  const [isEditMode, setEditMode] = useState(false);
  const [selectedIds, setSelected] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [showGroupUpdate, setShowGroupUpdate] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'success',
  });

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert((a) => ({ ...a, show: false })), 4000);
  };

  // ── Debounce search → a server-side ?q= (was client-only, so it only
  // ever searched whichever page happened to be loaded) ───────────────
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Any filter change starts back at page 1
  useEffect(() => {
    setPage(1);
  }, [year, month, type, category, subcategory, useDateRange, dateFrom, dateTo, debouncedSearch]);

  // ── Fetch Categories ─────────────────────────────────────────
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryAPI.list();
        const cats = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        setCategories(cats);
        await cacheCategories(cats);
      } catch (err) {
        if (isNetworkError(err)) {
          // Offline (#22): the filter dropdown still needs options to show.
          setCategories(await getCachedCategories());
        } else {
          console.error('Failed to load categories');
        }
      }
    };
    fetchCategories();
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────
  // Shared with export() (#32) so "export respects the current filter"
  // actually means the same filter the list is showing, not a second copy
  // of this logic that could quietly drift from it.
  const buildFilterParams = useCallback(() => {
    const params = {
      ...(type && { type }),
      ...(category && { category: category }),
      ...(subcategory && { subcategory: subcategory }),
      ...(debouncedSearch && { q: debouncedSearch }),
    };
    const { start, end } = useDateRange
      ? { start: dateFrom, end: dateTo }
      : getYearOrMonthRange(year, month > 0 ? month : null);
    params.date_from = start;
    params.date_to = end;
    return params;
  }, [type, category, subcategory, debouncedSearch, useDateRange, dateFrom, dateTo, year, month]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Backend only understands date_from/date_to — translate the
      // year/month picker into a range instead of sending year/month
      // directly (those aren't real filter params and were silently
      // ignored, so this mode never actually filtered anything).
      const params = {
        ordering: '-date',
        page,
        page_size: PAGE_SIZE,
        ...buildFilterParams(),
      };

      const res = await transactionAPI.list(params);
      // res.data is {count, next, previous, results} now that the
      // endpoint is paginated (#15) — the array fallback covers any
      // other list endpoint reusing this same list() helper.
      const data = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      setTransactions(data);
      setPageInfo({
        count: res.data?.count ?? data.length,
        hasNext: !!res.data?.next,
        hasPrevious: !!res.data?.previous,
      });
      await cacheTransactions(data);
    } catch (err) {
      if (isNetworkError(err)) {
        // Offline (#22): best-effort recent view, not the current filter's
        // real result — there's no server to ask, so show what's cached.
        const cached = await getCachedTransactions();
        setTransactions(cached);
        setPageInfo({ count: cached.length, hasNext: false, hasPrevious: false });
        setError('Showing cached data — you are offline.');
      } else {
        setError('Failed to load transactions.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, buildFilterParams]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // ── Helper: Get categories filtered by type ────────────────────
  const getCategoriesByType = (typeFilter) => {
    if (!typeFilter) return categories; // Show all if no type selected

    // Map transaction type to category_type
    const typeMap = {
      'INCOME': 'INCOME',
      'EXPENSE': 'EXPENSE',
      'SAVING': 'SAVING',
      'TRANSFER': 'TRANSFER',
    };

    const categoryType = typeMap[typeFilter];
    return categories.filter((c) => c.category_type === categoryType);
  };

  // Search now runs server-side (see fetchTransactions's `q` param) so it
  // covers the whole filtered set, not just whichever page is loaded.

  // ── Selection helpers — scoped to the current page (#15) ───────────
  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

  const toggleSelectAll = () =>
    setSelected(
      selectedIds.length === transactions.length ? [] : transactions.map((t) => t.id),
    );

  // ── Delete ────────────────────────────────────────────────────
  // Refetch rather than patch local state — with pagination, a deletion
  // can change the total count and which rows belong on this page. Offline
  // (#22) is the one exception: the server hasn't actually deleted anything
  // yet, so a refetch would just show the row still there — remove it from
  // local state instead and let the real delete happen on reconnect.
  const queueOfflineDelete = async (id) => {
    const localPlaceholderRef =
      typeof id === 'string' && id.startsWith('local-') ? id.slice('local-'.length) : null;
    await enqueue({
      entity: 'transaction',
      op: 'delete',
      targetId: localPlaceholderRef ? null : id,
      dependsOnClientRef: localPlaceholderRef,
    });
    setTransactions((prev) => prev.filter((t) => t.id !== id));
  };

  const handleDelete = async (id) => {
    try {
      await transactionAPI.delete(id);
      await fetchTransactions();
      showAlert('Transaction deleted.');
    } catch (err) {
      if (isNetworkError(err)) {
        await queueOfflineDelete(id);
        showAlert('Deleted — will sync when back online.');
      } else if (err.response?.status === 404) {
        // Already gone — e.g. it was a transfer leg (#31) and deleting its
        // sibling cascaded to this one too. The row being gone is exactly
        // what was asked for, not a failure.
        await fetchTransactions();
        showAlert('Transaction deleted.');
      } else {
        showAlert('Failed to delete transaction.', 'error');
      }
    } finally {
      setConfirmId(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => transactionAPI.delete(id)),
      );
      const networkFailed = [];
      const succeeded = [];
      let otherFailed = false;
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          if (isNetworkError(r.reason)) networkFailed.push(selectedIds[i]);
          else if (r.reason?.response?.status === 404) {
            // Already gone — selecting both legs of a transfer (#31)
            // means deleting the first one cascades to the second, so its
            // own delete call 404s. The goal (both gone) is still met.
            succeeded.push(selectedIds[i]);
          } else otherFailed = true;
        } else {
          succeeded.push(selectedIds[i]);
        }
      });

      if (networkFailed.length > 0) {
        // Can't refetch here: the server still has the queued-offline rows,
        // so a refetch would just bring them right back. Filter both kinds
        // out of local state instead (queueOfflineDelete does its own).
        await Promise.all(networkFailed.map(queueOfflineDelete));
        setTransactions((prev) => prev.filter((t) => !succeeded.includes(t.id)));
      } else {
        await fetchTransactions();
      }

      setSelected([]);
      setEditMode(false);
      if (otherFailed) showAlert('Some deletions failed.', 'error');
      else if (networkFailed.length > 0) showAlert(`${selectedIds.length} transaction(s) deleted — some will sync when back online.`);
      else showAlert(`${selectedIds.length} transaction(s) deleted.`);
    } catch {
      showAlert('Some deletions failed.', 'error');
    }
  };

  // ── Export / re-import (#32) ────────────────────────────────────
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const handleExport = async () => {
    try {
      const res = await transactionAPI.export(buildFilterParams());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'transactions.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showAlert('Export failed.', 'error');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;

    setImporting(true);
    try {
      const res = await transactionAPI.importEdits(file);
      const { updated_count, errors } = res.data;
      if (errors.length === 0) {
        showAlert(`Updated ${updated_count} transaction(s).`);
      } else {
        showAlert(
          `Updated ${updated_count}, ${errors.length} row(s) had errors (e.g. "${errors[0].error}").`,
          'error',
        );
      }
      await fetchTransactions();
    } catch {
      showAlert('Import failed — check the file is a CSV exported from this page.', 'error');
    } finally {
      setImporting(false);
    }
  };

  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-4">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Transactions</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            title="Export the currently filtered transactions as CSV"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          >
            <Download size={13} /> Export
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleImportFile}
          />
          <button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            title="Re-import a CSV previously exported from here, with your edits applied"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border bg-white text-gray-600 border-gray-200 hover:border-gray-300 disabled:opacity-50"
          >
            <Upload size={13} /> {importing ? 'Importing…' : 'Import edits'}
          </button>
          <button
            onClick={() => {
              setEditMode(!isEditMode);
              setSelected([]);
            }}
            className={classNames(
              'px-3 py-1.5 text-sm rounded-lg border transition-colors',
              isEditMode
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
            )}
          >
            {isEditMode ? (
              'Done'
            ) : (
              <>
                <Pencil size={13} className="inline mr-1" />
                Edit
              </>
            )}
          </button>
          <button
            onClick={() => {
              setEditTarget(null);
              setShowForm(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
          >
            <Plus size={15} /> Add
          </button>
        </div>
      </div>

      {/* ── Alert ──────────────────────────────────────────────── */}
      {alert.show && (
        <div
          className={classNames(
            'px-4 py-2.5 rounded-lg text-sm',
            alert.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200',
          )}
        >
          {alert.message}
        </div>
      )}

      {/* ── Filters ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        {/* Quick date filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Calendar size={16} className="text-gray-400" />
          <button
            onClick={() => {
              const today = new Date();
              const thirtyDaysBack = new Date();
              thirtyDaysBack.setDate(today.getDate() - 30);
              setDateFrom(thirtyDaysBack.toISOString().split('T')[0]);
              setDateTo(today.toISOString().split('T')[0]);
              setUseDateRange(true);
            }}
            className={classNames(
              'px-3 py-1.5 text-sm rounded-lg border transition-colors',
              useDateRange && dateFrom === new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300',
            )}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setUseDateRange(false)}
            className={classNames(
              'px-3 py-1.5 text-sm rounded-lg border transition-colors',
              !useDateRange
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300',
            )}
          >
            By Month/Year
          </button>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap gap-3 items-center">
          {useDateRange ? (
            <>
              {/* Date range inputs */}
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </>
          ) : (
            <>
              {/* Year */}
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>

              {/* Month */}
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i}>
                    {m}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Type */}
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              // Reset category and subcategory when type changes
              setCategory('');
              setSubcategory('');
            }}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Types</option>
            {['INCOME', 'EXPENSE', 'SAVING', 'TRANSFER'].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          {/* Category - Only show when a type is selected */}
          {type && (
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setSubcategory(''); // Reset subcategory when category changes
              }}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Categories</option>
              {getCategoriesByType(type).map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          )}

          {/* Subcategory - Only show when both type and category are selected */}
          {type && category && (
            <select
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Subcategories</option>
              {categories
                .find((c) => c.id === parseInt(category))
                ?.subcategories?.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.icon} {sub.name}
                  </option>
                ))}
            </select>
          )}

          {/* Search */}
          <input
            type="search"
            placeholder="Search by description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[140px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Results info + pager (#15) */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {pageInfo.count} transaction{pageInfo.count !== 1 ? 's' : ''} found
            {pageInfo.count > 0 &&
              ` — page ${page} of ${Math.max(1, Math.ceil(pageInfo.count / PAGE_SIZE))}`}
          </span>
          {(pageInfo.hasPrevious || pageInfo.hasNext) && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!pageInfo.hasPrevious || loading}
                className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!pageInfo.hasNext || loading}
                className="p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Bulk actions bar (edit mode) ──────────────────────── */}
      {isEditMode && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2 flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={
              selectedIds.length === transactions.length && transactions.length > 0
            }
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          />
          <span className="text-indigo-700 font-medium">
            {selectedIds.length} selected
            {transactions.length > 0 && ` (this page has ${transactions.length})`}
          </span>
          <button
            onClick={() => setShowGroupUpdate(true)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100 text-xs"
          >
            <Layers size={13} /> Group update…
          </button>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs"
            >
              <Trash2 size={13} /> Delete selected
            </button>
          )}
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────── */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          {error}
        </p>
      )}

      {/* ── Transaction list ───────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <TransactionList
          transactions={transactions}
          loading={loading}
          mobileLimit={10}
          tabletLimit={25}
          detailed={true}
          emptyMessage="No transactions match your filters."
          showAllByDefault={true}
          isEditMode={isEditMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onEdit={(txn) => {
            setEditTarget(txn);
            setShowForm(true);
          }}
          onDelete={(id) => setConfirmId(id)}
        />
      </div>

      {/* ── Add / Edit modal ───────────────────────────────────── */}
      {showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditTarget(null);
          }}
          title={editTarget ? 'Edit Transaction' : 'Add Transaction'}
        >
          <TransactionForm
            initial={editTarget ?? {}}
            onClose={() => {
              setShowForm(false);
              setEditTarget(null);
            }}
            onSuccess={() => {
              // Refetch rather than patch locally — a new/edited date can
              // move a row onto a different page than the one shown.
              fetchTransactions();
              setShowForm(false);
              setEditTarget(null);
              showAlert(
                editTarget ? 'Transaction updated.' : 'Transaction added.',
              );
            }}
          />
        </Modal>
      )}

      {/* ── Group update modal ─────────────────────────────────── */}
      <GroupUpdateModal
        isOpen={showGroupUpdate}
        onClose={() => setShowGroupUpdate(false)}
        categories={categories}
        onApplied={(result) => {
          fetchTransactions();
          showAlert(result.message);
        }}
      />

      {/* ── Delete confirm modal ───────────────────────────────── */}
      {
        <ConfirmModal
          isOpen={!!confirmId}
          onClose={() => setConfirmId(null)}
          onConfirm={() => handleDelete(confirmId)}
          title="Delete Transaction?"
          message={
            transactions.find((t) => t.id === confirmId)?.transfer_group
              // A transfer's two legs are deleted as a pair (#31) — the
              // backend cascades, so warn before the other side vanishes
              // too without being individually confirmed.
              ? "This is one side of a transfer — deleting it removes both sides. This action cannot be undone."
              : 'This action cannot be undone.'
          }
          confirmLabel="Delete"
          danger
        />
      }
    </div>
  );
}
